import React, { useState, useEffect } from 'react';
import { FormRenderer } from '../../../content-builder/FormRenderer';

/**
 * Step 2: Required Information Capture
 * 
 * Purpose: Render template form and collect field values with validation
 * 
 * Key Responsibilities:
 * - Display template content with form fields
 * - Automatically mount signature pads in signature placeholders
 * - Capture all form field values in real-time
 * - Validate form based on field requirements
 * - Store captured values and persist across step navigation
 */
const Step2_InformationCapture = ({
  wizardData,
  template,
  onDataChange,
  onValidationChange
}) => {
  const [formData, setFormData] = useState(wizardData.formData || {});
  const [isValid, setIsValid] = useState(false);

  // Handle form data changes from FormRenderer
  const handleFormDataChange = (capturedData) => {
    setFormData(capturedData);
    
    // Update parent with form data
    onDataChange({
      ...wizardData,
      formData: capturedData
    });
  };

  // Handle validation changes from FormRenderer
  const handleValidationChange = (isValid) => {
    setIsValid(isValid);
    onValidationChange(isValid);
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Complete Required Information</h3>
        <p className="text-sm text-blue-800">
          Please fill out all required fields. If this document requires a signature, sign below.
        </p>
      </div>

      {/* Form Renderer - handles form display, signature pads, and data capture */}
      {template ? (
        <div className="border rounded-lg p-6 bg-white">
          <FormRenderer
            content={(template.content || '').replace(
              /\[CLUB_NAME\]/g,
              wizardData.clubName || '[Club Name]'
            )}
            onFormDataChange={handleFormDataChange}
            onValidationChange={handleValidationChange}
            showSignature={true}
            acceptanceMode={template.acceptance_mode || 'accept'}
            documentTitle={template.title}
            initialFormData={formData}
          />
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Template content not available</p>
        </div>
      )}

      {/* Form Status */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        {isValid ? (
          <p className="text-green-600 font-semibold text-sm">✓ Form is ready to proceed</p>
        ) : (
          <p className="text-red-600 font-semibold text-sm">⚠️ Please complete all required fields</p>
        )}
      </div>
    </div>
  );
};

export { Step2_InformationCapture };